import {
  SCENARIO_MODEL_FLASH,
  SCENARIO_MODEL_FLASH_MID,
  SCENARIO_MODEL_PRO,
  SCENARIO_MODEL_ARGON,
  SCENARIO_MODEL_GPT6_ASTRA,
  SCENARIO_MODEL_GPT6_SOL,
  SCENARIO_MODEL_GPT6_LUNA,
  SCENARIO_MODEL_CLAUDE_SONNET,
  SCENARIO_MODEL_CLAUDE_OPUS,
  SCENARIO_MODEL_CLAUDE_FABLE,
  type ScenarioModelType,
} from '../state/useGenerationState';

/** モデル選択ドロップダウン左ラベルの品質ティア (2026-08: 高品質/標準/スピードの3段に統一)。 */
export type ScenarioModelTier = 'スピード' | '標準' | '高品質';

/**
 * シナリオ生成モデルのマスタ（単一の正本）。
 *
 * provider（提供会社）/ family（モデルファミリー）/ modelName（モデル名）を分離して持ち、
 * 表示文字列はここから導出する。モデルの追加・改名はこの配列だけを編集すればよい。
 * - フルネーム（"Claude Opus 5"）= モデル選択ドロップダウン / 生成中タブ / 失敗トースト
 *
 * 表示順は Claude → OpenAI → Gemini（ドロップダウンの並びと一致）。
 * 各プロバイダ内は高度（高額）なモデルが上（料金表ページの並びとも一致させる）。
 */
export interface ScenarioModelSpec {
  value: ScenarioModelType;
  provider: 'Anthropic' | 'OpenAI' | 'Google';
  family: string;   // 'Claude' / 'GPT' / 'Gemini'
  modelName: string; // 'Opus 5' / '5.4' / '3.6 Flash' など（family を含まないモデル名）
  icon: string;
  tier: ScenarioModelTier;
}

export const SCENARIO_MODEL_CATALOG: ScenarioModelSpec[] = [
  { value: SCENARIO_MODEL_CLAUDE_FABLE, provider: 'Anthropic', family: 'Claude', modelName: 'Fable 5.1', icon: '/claude_icon_mono.svg', tier: '高品質' },
  { value: SCENARIO_MODEL_CLAUDE_OPUS, provider: 'Anthropic', family: 'Claude', modelName: 'Opus 5.5', icon: '/claude_icon_mono.svg', tier: '標準' },
  { value: SCENARIO_MODEL_CLAUDE_SONNET, provider: 'Anthropic', family: 'Claude', modelName: 'Sonnet 5.5', icon: '/claude_icon_mono.svg', tier: 'スピード' },
  { value: SCENARIO_MODEL_GPT6_ASTRA, provider: 'OpenAI', family: 'GPT', modelName: '6 Astra', icon: '/openai_icon.svg', tier: '高品質' },
  { value: SCENARIO_MODEL_GPT6_SOL, provider: 'OpenAI', family: 'GPT', modelName: '6.1 Sol', icon: '/openai_icon.svg', tier: '標準' },
  { value: SCENARIO_MODEL_GPT6_LUNA, provider: 'OpenAI', family: 'GPT', modelName: '6 Luna', icon: '/openai_icon.svg', tier: 'スピード' },
  { value: SCENARIO_MODEL_ARGON, provider: 'Google', family: 'Gemini', modelName: '4 Argon', icon: '/gemini_icon_white.svg', tier: '高品質' },
  { value: SCENARIO_MODEL_PRO, provider: 'Google', family: 'Gemini', modelName: '3.1 Pro', icon: '/gemini_icon_white.svg', tier: '高品質' },
  { value: SCENARIO_MODEL_FLASH_MID, provider: 'Google', family: 'Gemini', modelName: '3.8 Flash', icon: '/gemini_icon_white.svg', tier: '標準' },
  { value: SCENARIO_MODEL_FLASH, provider: 'Google', family: 'Gemini', modelName: '3.5 Flash-Lite', icon: '/gemini_icon_white.svg', tier: 'スピード' },
];

const SPEC_BY_VALUE: Record<string, ScenarioModelSpec> = Object.fromEntries(
  SCENARIO_MODEL_CATALOG.map((s) => [s.value, s]),
);

/** family と modelName を結合したフルネーム。OpenAI のみ "GPT-5.4" のハイフン区切り。 */
export function scenarioModelFullName(spec: ScenarioModelSpec): string {
  const connector = spec.family === 'GPT' ? '-' : ' ';
  return `${spec.family}${connector}${spec.modelName}`;
}

/** モデル id → フルネーム（"Claude Opus 5"）。未知の id はそのまま返す。 */
export function scenarioModelDisplayName(value: string): string {
  const spec = SPEC_BY_VALUE[value];
  return spec ? scenarioModelFullName(spec) : value;
}

/** モデル id → 「{family}{tier}」の要約ラベル（"Claude高品質" / "GPT標準" 等）。未知の id はそのまま返す。 */
export function scenarioModelTierLabel(value: string): string {
  const spec = SPEC_BY_VALUE[value];
  return spec ? `${spec.family}${spec.tier}` : value;
}
